import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker, useNavigation } from "react-day-picker"
import { tr } from "date-fns/locale"
import { setMonth, setYear, addDays, startOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns"

import { cn, parseDateFromLocal } from "@/lib/utils"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

// Icon components
const IconLeft = ({ className: iconClassName, ...iconProps }) => (
  <ChevronLeft className={cn("h-4 w-4", iconClassName)} {...iconProps} />
);

const IconRight = ({ className: iconClassName, ...iconProps }) => (
  <ChevronRight className={cn("h-4 w-4", iconClassName)} {...iconProps} />
);

// Custom Caption component with month/year dropdowns in Turkish
function TurkishCaption({ displayMonth, fromYear, toYear }) {
  const { goToMonth } = useNavigation();
  
  const months = [
    "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
  ];
  
  const years = React.useMemo(() => {
    const arr = [];
    const start = fromYear || 1950;
    const end = toYear || new Date().getFullYear();
    for (let y = end; y >= start; y--) {
      arr.push(y);
    }
    return arr;
  }, [fromYear, toYear]);

  const handleMonthChange = (value) => {
    const newMonth = setMonth(displayMonth, parseInt(value));
    goToMonth(newMonth);
  };

  const handleYearChange = (value) => {
    const newMonth = setYear(displayMonth, parseInt(value));
    goToMonth(newMonth);
  };

  return (
    <div className="flex items-center justify-center gap-1 px-8">
      <Select
        value={displayMonth.getMonth().toString()}
        onValueChange={handleMonthChange}
      >
        <SelectTrigger className="h-7 w-[96px] text-sm font-bold text-[#1F2A44] border-0 bg-transparent focus:ring-0" data-testid="calendar-month-select">
          <SelectValue placeholder="Ay" />
        </SelectTrigger>
        <SelectContent>
          {months.map((m, index) => (
            <SelectItem key={index} value={index.toString()} className="text-xs">
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={displayMonth.getFullYear().toString()}
        onValueChange={handleYearChange}
      >
        <SelectTrigger className="h-7 w-[74px] text-sm font-bold text-[#1F2A44] border-0 bg-transparent focus:ring-0" data-testid="calendar-year-select">
          <SelectValue placeholder="Yıl" />
        </SelectTrigger>
        <SelectContent className="max-h-[200px]">
          {years.map((y) => (
            <SelectItem key={y} value={y.toString()} className="text-xs">
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Ay ve yıl seçicili gelişmiş takvim
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  locale = tr,
  selected,
  onSelect,
  defaultMonth,
  month,
  fromYear = 1950,
  toYear = new Date().getFullYear() + 5,
  showShortcuts = true,
  ...props
}) {
  // When the calendar (re)opens, show the SELECTED date's month instead of
  // jumping to today. Works for single date, range and multiple selections.
  const computedDefaultMonth = React.useMemo(() => {
    if (month !== undefined) return undefined; // controlled month takes over
    if (defaultMonth) return defaultMonth;
    if (selected instanceof Date) return selected;
    if (selected && selected.from instanceof Date) return selected.from;
    if (Array.isArray(selected) && selected[0] instanceof Date) return selected[0];
    if (typeof selected === "string") return parseDateFromLocal(selected) || undefined;
    return undefined;
  }, [month, defaultMonth, selected]);

  // Custom Caption wrapper that passes fromYear/toYear
  const CustomCaption = React.useCallback(
    (captionProps) => (
      <TurkishCaption 
        {...captionProps}
        fromYear={fromYear}
        toYear={toYear}
      />
    ),
    [fromYear, toYear]
  );

  const isRange = props.mode === "range";
  const shortcuts = React.useMemo(() => {
    if (!showShortcuts || !onSelect || !["single", "range"].includes(props.mode)) return [];
    const today = startOfDay(new Date());
    const blocked = (d) => props.disabled === true || (typeof props.disabled === "function" && props.disabled(d));
    if (isRange) {
      return [
        { label: "Bugün", from: today, to: today },
        { label: "Bu Hafta", from: startOfWeek(today, { weekStartsOn: 1 }), to: endOfWeek(today, { weekStartsOn: 1 }) },
        { label: "Bu Ay", from: startOfMonth(today), to: endOfMonth(today) },
      ].filter((x) => !blocked(x.from) && !blocked(x.to));
    }
    return [
      { label: "Bugün", from: today },
      { label: "Yarın", from: addDays(today, 1) },
      { label: "1 Hafta Sonra", from: addDays(today, 7) },
    ].filter((x) => !blocked(x.from));
  }, [showShortcuts, onSelect, props.mode, props.disabled, isRange]);

  const pick = (x) => (isRange ? onSelect({ from: x.from, to: x.to }, x.to, {}, undefined) : onSelect(x.from, x.from, {}, undefined));

  return (
    <div className="pb-calendar">
      <DayPicker
        locale={locale}
        weekStartsOn={1}
        showOutsideDays={showOutsideDays}
        selected={selected}
        onSelect={onSelect}
        month={month}
        defaultMonth={computedDefaultMonth}
        className={cn("p-3", className)}
        classNames={{
          months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
          month: "space-y-3",
          caption: "flex justify-center pt-1 relative items-center",
          caption_label: "hidden",
          nav: "space-x-1 flex items-center",
          nav_button: "h-7 w-7 inline-flex items-center justify-center rounded-lg border border-stone-200 bg-white text-[#1F2A44] hover:bg-emerald-50 hover:border-emerald-400 transition-colors",
          nav_button_previous: "absolute left-1",
          nav_button_next: "absolute right-1",
          table: "w-full border-collapse",
          head_row: "flex",
          head_cell: "w-9 text-[11px] font-semibold uppercase tracking-wide text-stone-500",
          row: "flex w-full mt-1",
          cell: cn(
            "relative p-0.5 text-center text-sm focus-within:relative focus-within:z-20",
            isRange && "[&:has([aria-selected].day-range-middle)]:bg-emerald-50 first:[&:has([aria-selected])]:rounded-l-xl last:[&:has([aria-selected])]:rounded-r-xl"
          ),
          day: "h-9 w-9 p-0 rounded-xl font-semibold text-[#1F2A44] hover:bg-emerald-50 hover:text-emerald-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 aria-selected:opacity-100",
          day_range_start: "day-range-start",
          day_range_end: "day-range-end",
          day_selected: "!bg-emerald-600 !text-white hover:!bg-emerald-700 shadow-sm",
          day_today: "ring-1 ring-inset ring-emerald-500 text-emerald-700",
          day_outside: "day-outside !text-stone-300 !font-normal aria-selected:!bg-emerald-100 aria-selected:!text-emerald-700",
          day_disabled: "!text-stone-300 !font-normal opacity-60 hover:bg-transparent cursor-not-allowed",
          day_range_middle: "aria-selected:!bg-emerald-50 aria-selected:!text-emerald-800 !rounded-none !shadow-none",
          day_hidden: "invisible",
          ...classNames,
        }}
        components={{
          IconLeft,
          IconRight,
          Caption: CustomCaption,
        }}
        {...props} />
      {shortcuts.length > 0 && (
        <div className="flex gap-1.5 px-3 pb-3 -mt-1" data-testid="calendar-shortcuts">
          {shortcuts.map((x) => (
            <button key={x.label} type="button" onClick={() => pick(x)}
              className="flex-1 rounded-lg border border-stone-200 bg-white px-2 py-1.5 text-xs font-medium text-[#1F2A44] hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-800 transition-colors"
              data-testid={`calendar-shortcut-${x.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {x.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
Calendar.displayName = "Calendar"

export { Calendar }
